import { Link, useLocation } from 'react-router-dom';
import { Users, ClipboardList, Cpu, ArrowLeft, CheckCircle, RotateCcw } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const STEPS = [
  { id: 'people', label: 'Mensen', href: '/people', icon: Users, stepNum: 1 },
  { id: 'process', label: 'Organisatie', href: '/process', icon: ClipboardList, stepNum: 2 },
  { id: 'technology', label: 'Systemen', href: '/technology', icon: Cpu, stepNum: 3 },
];

const WizardStepper = ({ moduleStats }) => {
  const location = useLocation();
  const { api } = useAuth();
  const [activeReassessment, setActiveReassessment] = useState(null);

  useEffect(() => {
    const fetchReassessment = async () => {
      try {
        const res = await api.get('/assessments/reassessments');
        const active = (res.data || []).find(r => r.status === 'in_progress');
        setActiveReassessment(active || null);
      } catch { /* ignore */ }
    };
    fetchReassessment();
  }, [api]);

  const isModuleReassessing = (id) => {
    if (!activeReassessment) return false;
    return (activeReassessment.modules || []).includes(id) || (activeReassessment.modules || []).includes('all');
  };

  const isSteekproef = !!activeReassessment;

  const getModuleSteps = (id) => {
    const s = moduleStats?.[id];
    const reassessing = isModuleReassessing(id);
    if (id === 'people') {
      const hrDone = !reassessing && s?.hr_assessment_status === 'completed';
      const hasCampaign = !reassessing && (s?.total_campaigns || 0) > 0;
      const hasSent = !reassessing && (s?.total_sent || 0) > 0;
      return [{ done: hrDone }, { done: hasCampaign }, { done: hasSent }];
    }
    if (id === 'process') {
      const completed = reassessing ? 0 : (s?.completed || 0);
      const inProgress = reassessing ? 0 : (s?.in_progress || 0);
      return [{ done: completed > 0 || inProgress > 0 }, { done: completed > 0 }, { done: completed >= 2 }];
    }
    if (id === 'technology') {
      const hasTargets = !reassessing && (s?.total_targets || 0) > 0;
      const hasScans = !reassessing && (s?.completed_scans || 0) > 0;
      return [{ done: hasTargets }, { done: hasScans }, { done: hasScans && (s?.total_vulnerabilities || 0) > 0 }];
    }
    return [];
  };

  const getProgress = (id) => {
    const steps = getModuleSteps(id);
    if (!steps.length) return 0;
    return Math.round((steps.filter(s => s.done).length / steps.length) * 100);
  };

  const getStatus = (id) => {
    if (isModuleReassessing(id)) return 'reassessing';
    const p = getProgress(id);
    return p === 0 ? 'not_started' : p >= 100 ? 'completed' : 'in_progress';
  };

  const currentStep = STEPS.find(s => location.pathname === s.href);
  const currentIdx = currentStep ? STEPS.indexOf(currentStep) : 0;

  return (
    <div className="mb-8" data-testid="wizard-stepper">
      {/* Top bar */}
      <div className="flex items-center justify-between mb-4">
        <Link
          to="/assessment"
          className="flex items-center gap-2 text-sm text-neutral-500 hover:text-black transition-colors"
          data-testid="wizard-back-to-overview"
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
          Terug naar overzicht
        </Link>
        {isSteekproef && (
          <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 border border-amber-200">
            <RotateCcw className="h-3 w-3 text-amber-600" strokeWidth={2} />
            <span className="text-[11px] font-semibold text-amber-700">Steekproef</span>
          </div>
        )}
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-0" data-testid="wizard-steps">
        {STEPS.map((step, idx) => {
          const status = getStatus(step.id);
          const isActive = idx === currentIdx;
          const isCompleted = status === 'completed';
          const isReassessing = status === 'reassessing';
          const isPast = idx < currentIdx;

          return (
            <div key={step.id} className="flex items-center flex-1">
              <Link
                to={step.href}
                className={`flex items-center gap-3 flex-1 px-4 py-3 rounded-xl transition-all ${
                  isActive
                    ? 'bg-black text-white shadow-md'
                    : isCompleted
                      ? 'bg-green-50 text-green-800 border border-green-200 hover:bg-green-100'
                      : isReassessing
                        ? 'bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100'
                        : 'bg-neutral-50 text-neutral-500 border border-neutral-100 hover:bg-neutral-100 hover:text-neutral-700'
                }`}
                data-testid={`wizard-step-${step.id}`}
              >
                <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 text-sm font-bold ${
                  isActive ? 'bg-white/20 text-white' :
                  isCompleted ? 'bg-green-200 text-green-700' :
                  isReassessing ? 'bg-amber-200 text-amber-700' :
                  'bg-neutral-200 text-neutral-500'
                }`}>
                  {isCompleted ? <CheckCircle className="h-4 w-4" strokeWidth={2} /> :
                   isReassessing ? <RotateCcw className="h-3.5 w-3.5" strokeWidth={2} /> :
                   step.stepNum}
                </div>
                <div className="min-w-0 flex-1">
                  <p className={`text-[10px] uppercase tracking-wider font-bold ${
                    isActive ? 'text-white/70' : 'text-neutral-400'
                  }`}>Stap {step.stepNum} van 3</p>
                  <p className={`text-sm font-semibold truncate ${
                    isActive ? 'text-white' : ''
                  }`}>{step.label}</p>
                </div>
              </Link>
              {idx < STEPS.length - 1 && (
                <div className="w-4 flex items-center justify-center shrink-0">
                  <div className={`w-4 h-0.5 ${isPast || isCompleted ? 'bg-green-400' : isActive ? 'bg-neutral-300' : 'bg-neutral-200'}`} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WizardStepper;
export { STEPS };
